import { Link } from "react-router-dom";
import Button from "@/components/base/Button";
import SectionHeading from "@/components/base/SectionHeading";
import BlogImage from "@/components/feature/BlogImage";
import { blogPosts } from "@/data/blogs";
import { formatDate } from "@/lib/format";

export default function BlogSection() {
  const [featured, ...rest] = blogPosts;
  const secondary = rest.slice(0, 2);

  return (
    <section id="blog" className="relative scroll-mt-28 overflow-hidden bg-gradient-to-b from-accent-50/70 via-background-50 to-background-50 py-16 md:py-24">
      <div className="absolute -right-20 top-10 h-80 w-80 rounded-full bg-accent-200/30 blur-3xl" aria-hidden="true"></div>
      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-end">
          <SectionHeading
            eyebrow="Health Information"
            title="Insights for Better Orthopaedic Health"
            align="left"
            description="Practical, medically responsible guidance to help you understand your condition and your options."
          />
          <Button to="/blog" variant="outline" size="md" icon="ri-article-line">
            View All Articles
          </Button>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <article className="reveal group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50">
            <Link to={`/blog/${featured.slug}`} className="block cursor-pointer overflow-hidden">
              <div className="h-[260px] w-full overflow-hidden md:h-[340px]">
                <BlogImage
                  src={featured.featuredImage}
                  alt={featured.imageAlt}
                  title={`${featured.title} — Ortho Spine Care blog`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
            </Link>
            <div className="flex flex-1 flex-col p-6 md:p-7">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-semibold text-accent-800">
                  {featured.category}
                </span>
                <span className="font-label text-xs text-foreground-500">
                  {formatDate(featured.publishedDate)} · {featured.readTime}
                </span>
              </div>
              <h3 className="mt-4 font-heading text-xl font-semibold leading-snug text-foreground-950 md:text-2xl">
                {featured.title}
              </h3>
              <p className="mt-3 flex-1 font-body text-sm leading-relaxed text-foreground-600">{featured.excerpt}</p>
              <Link
                to={`/blog/${featured.slug}`}
                className="mt-6 inline-flex cursor-pointer items-center gap-2 font-label text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
              >
                Read more
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </article>

          <div className="flex flex-col gap-5">
            {secondary.map((post) => (
              <article
                key={post.slug}
                className="reveal group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 sm:flex-row"
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="block w-full cursor-pointer overflow-hidden sm:w-[42%]"
                >
                  <div className="h-[180px] w-full overflow-hidden sm:h-full sm:min-h-[200px]">
                    <BlogImage
                      src={post.featuredImage}
                      alt={post.imageAlt}
                      title={`${post.title} — Ortho Spine Care blog`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                </Link>
                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs font-semibold text-secondary-900">
                      {post.category}
                    </span>
                    <span className="font-label text-xs text-foreground-500">{formatDate(post.publishedDate)}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950">
                    {post.title}
                  </h3>
                  <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-foreground-600">{post.excerpt}</p>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="mt-4 inline-flex cursor-pointer items-center gap-2 font-label text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
                  >
                    Read more
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}